import { PitchDisplayPreset } from '@statsbomb/parachute-types';
import { VISUALISATION } from '@/consts/searchKeys';
import { ConfigDisplaySettingKey } from '@/types/userConfigs';
import { PitchCrop, PitchMarkerColouringMode, PitchOrientation } from '@/types/visualisation';

type VisType = 'eventMap' | 'heatMap';

export interface PitchVisDisplaySettings {
  pitchCrop: PitchCrop;
  pitchOrientation: PitchOrientation;
  showArrows: boolean;
  colouringMode: PitchMarkerColouringMode;
  visType: VisType;
  isStartPlacement: boolean;
}

const PRESETS: Record<PitchDisplayPreset, PitchVisDisplaySettings> = {
  SHOTS: {
    pitchCrop: 'OPPOSITION_BOX',
    pitchOrientation: 'VERTICAL',
    showArrows: false,
    colouringMode: 'xg',
    visType: 'eventMap',
    isStartPlacement: true,
  },
  SHOTS_CONCEDED: {
    pitchCrop: 'HOME_BOX',
    pitchOrientation: 'VERTICAL',
    showArrows: false,
    colouringMode: 'xg',
    visType: 'eventMap',
    isStartPlacement: true,
  },
  PASS_AND_CARRY: {
    pitchCrop: 'FULL_PITCH',
    pitchOrientation: 'HORIZONTAL',
    showArrows: true,
    colouringMode: 'passSuccess',
    visType: 'eventMap',
    isStartPlacement: true,
  },
  PASS_AND_CARRY_F3: {
    pitchCrop: 'OPPOSITION_THIRD',
    pitchOrientation: 'VERTICAL',
    showArrows: true,
    colouringMode: 'passSuccess',
    visType: 'eventMap',
    isStartPlacement: true,
  },
  PASS_AND_CARRY_FHALF: {
    pitchCrop: 'OPPOSITION_HALF',
    pitchOrientation: 'VERTICAL',
    showArrows: true,
    colouringMode: 'passSuccess',
    visType: 'eventMap',
    isStartPlacement: true,
  },
  HEATMAP: {
    pitchCrop: 'FULL_PITCH',
    pitchOrientation: 'HORIZONTAL',
    showArrows: false,
    colouringMode: 'default',
    visType: 'heatMap',
    isStartPlacement: true,
  },
  HEATMAP_F3: {
    pitchCrop: 'OPPOSITION_THIRD',
    pitchOrientation: 'VERTICAL',
    showArrows: false,
    colouringMode: 'default',
    visType: 'heatMap',
    isStartPlacement: true,
  },
  HEATMAP_FHALF: {
    pitchCrop: 'OPPOSITION_HALF',
    pitchOrientation: 'VERTICAL',
    showArrows: false,
    colouringMode: 'default',
    visType: 'heatMap',
    isStartPlacement: true,
  },
  HEATMAP_END_PLACEMENT: {
    pitchCrop: 'FULL_PITCH',
    pitchOrientation: 'HORIZONTAL',
    showArrows: false,
    colouringMode: 'default',
    visType: 'heatMap',
    isStartPlacement: false,
  },
  GOALKEEPER: {
    pitchCrop: 'HOME_THIRD',
    pitchOrientation: 'VERTICAL',
    showArrows: false,
    colouringMode: 'default',
    visType: 'eventMap',
    isStartPlacement: true,
  },
  GOALKEEPER_HALF: {
    pitchCrop: 'HOME_HALF',
    pitchOrientation: 'VERTICAL',
    showArrows: false,
    colouringMode: 'default',
    visType: 'eventMap',
    isStartPlacement: true,
  },
  DEFAULT: {
    pitchCrop: 'FULL_PITCH',
    pitchOrientation: 'HORIZONTAL',
    showArrows: false,
    colouringMode: 'default',
    visType: 'eventMap',
    isStartPlacement: true,
  },
  DEFAULT_ARROWS: {
    pitchCrop: 'FULL_PITCH',
    pitchOrientation: 'HORIZONTAL',
    showArrows: true,
    colouringMode: 'default',
    visType: 'eventMap',
    isStartPlacement: true,
  },
};

export const getMetricPitchConfig = (presetName?: PitchDisplayPreset): PitchVisDisplaySettings =>
  presetName ? PRESETS[presetName] : PRESETS.DEFAULT;

/**
 * Maps every field of PitchVisDisplaySettings to its corresponding URL param key.
 * Typed as Record<keyof PitchVisDisplaySettings, string> so TypeScript enforces that this
 * mapping stays complete if new fields are added to PitchVisDisplaySettings.
 * Both usePitchVisDisplaySettingsNavigation and useInitPitchDisplayPreset derive their
 * param lists from this — updating it here is sufficient to keep both hooks correct.
 */
export const PITCH_PRESET_PARAM_KEYS: Record<keyof PitchVisDisplaySettings, string> = {
  pitchCrop: ConfigDisplaySettingKey.PITCH_CROP,
  pitchOrientation: ConfigDisplaySettingKey.PITCH_ORIENTATION,
  showArrows: ConfigDisplaySettingKey.SHOW_ARROWS,
  colouringMode: ConfigDisplaySettingKey.PITCH_MARKER_COLOURING_MODE,
  visType: VISUALISATION,
  isStartPlacement: ConfigDisplaySettingKey.IS_START_PLACEMENT,
};
